import { LessonFolder } from '@/models/lessonFolder';
import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';
import {
    CREATE_LESSON_FOLDER,
    CreateLessonFolderResponse,
    CreateLessonFolderVariables,
    DELETE_LESSON_FOLDERS,
    DeleteLessonFoldersResponse,
    DeleteLessonFoldersVariables,
    GET_LESSON_FOLDERS,
    GET_LESSON_FOLDERS_COUNT,
    GetLessonFoldersCountResponse,
    GetLessonFoldersResponse,
    UPDATE_LESSON_FOLDER,
    UpdateLessonFolderResponse,
    UpdateLessonFolderVariables,
} from './gql';

export const useLessonFolders = (filters: unknown, page: { size: number; index: number }) => {
    const { data, loading, refetch } = useQuery<GetLessonFoldersResponse>(GET_LESSON_FOLDERS, {
        variables: {
            where: {
                AND: [...(filters ? [filters] : [])],
            },
            orderBy: [
                {
                    name: 'asc',
                },
            ],
            skip: page ? page.index * page.size : undefined,
            take: page ? page.size : undefined,
        },
        fetchPolicy: 'network-only',
    });

    const [folders, setFolders] = useState<LessonFolder[] | undefined>(undefined);

    // Fetch folders count
    const { data: foldersCountResponse, refetch: refetchLessonFoldersCount } = useQuery<GetLessonFoldersCountResponse>(GET_LESSON_FOLDERS_COUNT, {
        variables: {
            where: {
                AND: [...(filters ? [filters] : [])],
            },
        },
        fetchPolicy: 'network-only',
    });

    const [createLessonFolder] = useMutation<CreateLessonFolderResponse, CreateLessonFolderVariables>(CREATE_LESSON_FOLDER);
    const [updateLessonFolder] = useMutation<UpdateLessonFolderResponse, UpdateLessonFolderVariables>(UPDATE_LESSON_FOLDER);
    const [deleteLessonFolder] = useMutation<DeleteLessonFoldersResponse, DeleteLessonFoldersVariables>(DELETE_LESSON_FOLDERS);

    const create = useCallback(
        async (name: string) => {
            const resp = await createLessonFolder({
                variables: {
                    data: {
                        name: name,
                    },
                },
            });

            await Promise.allSettled([refetchLessonFoldersCount()]);

            return resp.data?.createLessonFolder ? new LessonFolder({ ...resp.data.createLessonFolder }) : null;
        },
        [createLessonFolder, refetchLessonFoldersCount],
    );

    const update = useCallback(
        async (id: string, name: string) => {
            const resp = await updateLessonFolder({
                variables: {
                    data: {
                        name: name,
                    },
                    where: {
                        id: id,
                    },
                },
            });

            return resp.data?.updateLessonFolder ? new LessonFolder({ ...resp.data.updateLessonFolder }) : null;
        },
        [updateLessonFolder],
    );

    const remove = useCallback(
        async (id: string[]) => {
            const resp = await deleteLessonFolder({
                variables: {
                    where: id.map((id) => ({ id })),
                },
            });

            await Promise.allSettled([refetchLessonFoldersCount()]);

            return resp.data?.deleteLessonFolders ? resp.data.deleteLessonFolders.map((folder) => new LessonFolder({ ...folder })) : null;
        },
        [deleteLessonFolder, refetchLessonFoldersCount],
    );

    useEffect(() => {
        if (data && !loading) {
            const newFolders: LessonFolder[] = data.lessonFolders.map((folder) => new LessonFolder({ ...folder }));
            setFolders(newFolders);
        }
    }, [data, loading]);

    return {
        folders,
        loading,
        create,
        update,
        remove,
        refetch,
        total: foldersCountResponse?.lessonFoldersCount,
    };
};
